import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { apiCall } from '../lib/api';
import {
  StarIcon, GiftIcon, TicketIcon, SparklesIcon, ClockIcon, CrownIcon,
  ArrowRightIcon,
} from '../components/icons';

interface PointsResponse {
  points: number;
  tier: string;
  summary: Record<string, { sum: number; count: number }>;
  progress: {
    current_tier: string;
    current_min: number;
    current_color: string | null;
    next_tier: string | null;
    next_min: number | null;
    points_to_next: number;
    pct: number;
  };
  active_formula: { name: string; baht_per_point: number; cycle_end: string } | null;
  ledger: Array<{
    id: number;
    type: string;
    points: number;
    balance_after: number;
    description: string;
    effective_at: string;
  }>;
}

const TYPE_META: Record<string, { label: string; bg: string; fg: string }> = {
  earn:         { label: 'จากการซื้อ',     bg: 'bg-emerald-50', fg: 'text-emerald-700' },
  welcome:      { label: 'Welcome Bonus',  bg: 'bg-blue-50',    fg: 'text-blue-700' },
  birthday:     { label: 'วันเกิด',        bg: 'bg-pink-50',    fg: 'text-pink-700' },
  spend_coupon: { label: 'แลกคูปอง',       bg: 'bg-amber-50',   fg: 'text-amber-700' },
  spend_reward: { label: 'แลกของรางวัล',   bg: 'bg-red-50',     fg: 'text-red-700' },
  adjust:       { label: 'ปรับโดย admin',  bg: 'bg-sky-50',     fg: 'text-sky-700' },
  expire:       { label: 'หมดอายุ',        bg: 'bg-gray-100',   fg: 'text-gray-600' },
  tier_bonus:   { label: 'โบนัส Tier',     bg: 'bg-emerald-50', fg: 'text-emerald-700' },
};

const TIER_GRADIENT: Record<string, string> = {
  Crystal:  'from-rose-500 to-red-600',
  Silver:   'from-gray-400 to-gray-600',
  Gold:     'from-amber-400 to-yellow-600',
  Platinum: 'from-slate-700 to-slate-900',
};

export function Points() {
  const q = useQuery<PointsResponse>({
    queryKey: ['points', 'me'],
    queryFn: () => apiCall<PointsResponse>('get', '/customers/me/points'),
  });

  if (q.isLoading) return <div className="p-6 text-center text-gray-500">กำลังโหลด...</div>;
  if (!q.data) return <div className="p-6 text-center text-gray-500">ไม่พบข้อมูล</div>;

  const d = q.data;
  const tierGrad = TIER_GRADIENT[d.progress.current_tier] ?? TIER_GRADIENT.Crystal;

  return (
    <div className="max-w-md mx-auto px-4 py-4 space-y-3 pb-24">
      <h1 className="text-xl font-bold">แต้มสะสมของฉัน</h1>

      {/* Hero — total + tier + progress */}
      <div className={`card text-white bg-gradient-to-br ${tierGrad}`}>
        <div className="flex items-start justify-between">
          <div>
            <div className="text-xs uppercase tracking-wider opacity-80">คะแนนคงเหลือ</div>
            <div className="text-4xl font-bold mt-1">{d.points.toLocaleString('th-TH')}</div>
            <div className="text-xs opacity-80 mt-1">คะแนน</div>
          </div>
          <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/20 backdrop-blur">
            <CrownIcon className="w-4 h-4" />
            <span className="text-sm font-bold">{d.progress.current_tier}</span>
          </div>
        </div>

        {/* Progress bar to next tier */}
        {d.progress.next_tier && d.progress.next_min !== null ? (
          <div className="mt-4">
            <div className="flex justify-between text-xs mb-1.5 opacity-90">
              <span>{d.progress.current_tier}</span>
              <span>{d.progress.next_tier}</span>
            </div>
            <div className="h-2 bg-white/20 rounded-full overflow-hidden">
              <div className="h-full bg-white rounded-full transition-all"
                   style={{ width: `${d.progress.pct}%` }} />
            </div>
            <div className="text-xs mt-1.5 opacity-90">
              อีก <b>{d.progress.points_to_next.toLocaleString('th-TH')}</b> คะแนน → {d.progress.next_tier}
            </div>
          </div>
        ) : (
          <div className="mt-4 text-xs opacity-90">
            🎉 คุณคือ Tier สูงสุดของระบบแล้ว
          </div>
        )}
      </div>

      {/* Active formula */}
      {d.active_formula && (
        <div className="card flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-amber-50 text-amber-700 flex items-center justify-center">
            <SparklesIcon className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold truncate">{d.active_formula.name}</div>
            <div className="text-xs text-gray-500">
              ใช้ <b>{d.active_formula.baht_per_point.toLocaleString('th-TH')} บาท</b> = 1 คะแนน
              · รอบสะสมถึง {new Date(d.active_formula.cycle_end).toLocaleDateString('th-TH')}
            </div>
          </div>
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-2 gap-2">
        {Object.entries(d.summary).map(([type, v]) => {
          const meta = TYPE_META[type] ?? { label: type, bg: 'bg-gray-100', fg: 'text-gray-700' };
          const sum = Number(v.sum);
          return (
            <div key={type} className={`card ${meta.bg}`}>
              <div className={`text-xs font-semibold ${meta.fg}`}>{meta.label}</div>
              <div className={`text-2xl font-bold mt-1 ${meta.fg}`}>
                {sum > 0 ? `+${sum.toLocaleString('th-TH')}` : sum.toLocaleString('th-TH')}
              </div>
              <div className="text-[11px] text-gray-500 mt-0.5">{v.count} รายการ</div>
            </div>
          );
        })}
      </div>

      {/* Quick actions */}
      <div className="grid grid-cols-2 gap-2">
        <Link to="/coupons" className="card flex items-center justify-between bg-gradient-to-r from-cyan-500 to-teal-600 text-white">
          <div className="flex items-center gap-2">
            <TicketIcon className="w-5 h-5" />
            <span className="text-sm font-bold">รับคูปอง</span>
          </div>
          <ArrowRightIcon className="w-4 h-4 opacity-80" />
        </Link>
        <Link to="/rewards" className="card flex items-center justify-between bg-gradient-to-r from-rose-500 to-pink-600 text-white">
          <div className="flex items-center gap-2">
            <GiftIcon className="w-5 h-5" />
            <span className="text-sm font-bold">แลกรางวัล</span>
          </div>
          <ArrowRightIcon className="w-4 h-4 opacity-80" />
        </Link>
      </div>

      {/* Ledger history */}
      <div className="card p-0 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
          <ClockIcon className="w-4 h-4 text-gray-500" />
          <span className="font-semibold text-sm">ประวัติแต้ม</span>
          <span className="text-xs text-gray-400 ml-auto">{d.ledger.length} รายการ</span>
        </div>
        {d.ledger.length === 0 ? (
          <div className="py-12 text-center text-gray-400 text-sm">
            <StarIcon className="w-12 h-12 mx-auto mb-2 text-gray-200" />
            ยังไม่มีรายการแต้ม
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {d.ledger.map((l) => {
              const meta = TYPE_META[l.type] ?? { label: l.type, bg: 'bg-gray-100', fg: 'text-gray-700' };
              return (
                <div key={l.id} className="px-4 py-3 flex items-start gap-3">
                  <div className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${meta.bg} ${meta.fg} flex-shrink-0`}>
                    {meta.label}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm break-words">{l.description || '—'}</div>
                    <div className="text-[11px] text-gray-400 mt-0.5">
                      {new Date(l.effective_at).toLocaleString('th-TH', {
                        year: 'numeric', month: 'short', day: 'numeric',
                        hour: '2-digit', minute: '2-digit',
                      })}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className={`font-bold text-sm ${l.points >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {l.points > 0 ? `+${l.points.toLocaleString('th-TH')}` : l.points.toLocaleString('th-TH')}
                    </div>
                    <div className="text-[11px] text-gray-400">
                      = {l.balance_after.toLocaleString('th-TH')}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
